import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import resourceAllocationService from '../../../services/resourceAllocationService'
import { 
  Users,
  CheckCircle,
  Clock,
  AlertTriangle,
  TrendingUp,
  Calendar,
  FileText,
  Award,
  BarChart3
} from 'lucide-react'

const ResourceAllocations = () => {
  const navigate = useNavigate()
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const response = await resourceAllocationService.getResourceAllocationStats()
      if (response.success) {
        setDashboardData(response.data)
      } else {
        if (!response._errorHandled) {
          toast.error(response.message || 'Failed to load dashboard data')
        }
      }
    } catch (error) {
      console.error('Error loading resource allocations dashboard data:', error)
      if (!error._errorHandled) {
        if (error.response) {
          toast.error(error.response.data?.message || 'Failed to load dashboard data')
        } else if (!error.request) {
          toast.error('Failed to load dashboard data')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="sales-dashboard">
      {/* Page Header */}
      <div className="page-header" style={{textAlign: 'left'}}>
        <div className="page-title" style={{textAlign: 'left', alignItems: 'flex-start'}}>
          <h1 style={{textAlign: 'left', margin: '0 0 4px 0'}}>Resource Allocation</h1>
          <p style={{textAlign: 'left', margin: 0}}>Manage resource assignments to projects</p>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">
            {loading ? '...' : (dashboardData?.totalAllocations || 0)}
          </div>
          <div className="metric-title">Total Allocations</div>
          <div className="metric-subtitle">All resource allocations</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">
            {loading ? '...' : (dashboardData?.active || 0)}
          </div>
          <div className="metric-title">Active</div>
          <div className="metric-subtitle">Currently active</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+5%</span>
            </div>
          </div>
          <div className="metric-value">
            {loading ? '...' : (dashboardData?.planned || 0)}
          </div>
          <div className="metric-title">Planned</div>
          <div className="metric-subtitle">Scheduled allocations</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertTriangle size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+3%</span>
            </div>
          </div>
          <div className="metric-value">
            {loading ? '...' : (dashboardData?.overdue || 0)}
          </div>
          <div className="metric-title">Overdue</div>
          <div className="metric-subtitle">Need attention</div>
        </div>
      </div>

      {/* Transactions Grid */}
      <div className="projects-transactions-content" style={{ marginTop: '24px' }}>
        <div className="projects-transactions-header">
          <h2>Resource Allocation Management</h2>
          <p>Track and manage all resource allocations</p>
        </div>
        <div className="projects-transactions-grid">
          <div 
            className="projects-transaction-card" 
            onClick={() => navigate('/projects/resource-allocations/list')} 
            style={{ cursor: 'pointer' }}
          >
            <div className="projects-transaction-icon">
              <Users size={24} />
            </div>
            <div className="projects-transaction-content">
              <h3>Resource Allocation List</h3>
              <p>Create, view, and manage all resource allocations</p>
              <div className="projects-transaction-count">
                {loading ? '...' : `${dashboardData?.totalAllocations || 0} Total`}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResourceAllocations

